import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useTransaction from 'Hooks/useTransaction';
import Loading from 'Components/Atoms/Loading/Loading';
import { useQueryClient } from '@tanstack/react-query';
import { CATEGORY_COLORS, DRAWER_STACK } from 'Utils/constants';
import DrawerStackSavingIndicator from 'Components/Organisms/DrawerStackProvider/DrawerStackSavingIndicator';
import useDrawerStack from 'Hooks/useDrawerStack';
import { createCategory } from 'Services/categories';
import useTaxonomies from 'Hooks/useTaxonomies';
import Tabs, { Tab } from 'Components/Molecules/Tabs/Tabs';
import Search from 'Components/Atoms/Search/Search';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import { useEffect, useState, Fragment } from 'react';
import { cn } from 'Utils/cn';
import useCategories from 'Hooks/useCategories';
import { hasMatch } from 'Utils/strings';
import Checkbox from 'Components/Atoms/Checkbox/Checkbox';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import { formatCurrency } from 'Utils/formatters';
import useLocale from 'Hooks/useLocale';
import Icon from 'Components/Atoms/Icon/Icon';

function filterByText(category, text) {
    const hasText = hasMatch(category.name, text);
    const hasSubcategoryText = category.subcategories.some((subcategory) => {
        return hasMatch(subcategory.name, text);
    });

    return hasText || hasSubcategoryText;
}

export default function TransactionTagsEditorDrawer(props) {
    const { transactionId, onTagsChange } = props;

    const { t } = useTranslation('categories');
    const queryClient = useQueryClient();
    const {
        transaction,
        isLoading: isTransactionLoading,
        updateTransaction,
        isUpdating,
    } = useTransaction(transactionId);
    const locale = useLocale();

    const { provider_connection_product, description, amount } = transaction;

    const { open } = useDrawerStack();
    const { taxonomies, isLoading: isTaxonomiesLoading } = useTaxonomies();

    const [currentTaxonomy, setCurrentTaxonomy] = useState(taxonomies[0]?.id ?? '');
    const [filterText, setFilterText] = useState('');
    const { categories } = useCategories();

    const parentCategories = categories.filter((category) => !category.parent_tag_id);
    const formattedCategories = parentCategories.map((category) => ({
        ...category,
        subcategories: categories.filter((subcategory) => subcategory.parent_tag_id === category.id),
    }));

    const categoriesFiltered = formattedCategories
        .filter((category) => category.taxonomy_id === currentTaxonomy)
        .filter((category) => filterByText(category, filterText))
        .map((category) => ({
            ...category,
            subcategories: category.subcategories.filter((subcategory) => hasMatch(subcategory.name, filterText)),
        }));

    const onChangeHandle = (id, isAdd) => {
        const tagsIds = Array.isArray(transaction.tags_ids) ? transaction.tags_ids : [];
        if (isAdd) {
            updateTransactionsTags([...tagsIds, id]);
            onTagsChange?.({ tagId: id });
        } else {
            const subcategoriesIds = formattedCategories
                .find((category) => category.id === id)
                .subcategories.map((subcategory) => subcategory.id);
            updateTransactionsTags(tagsIds.filter((tagId) => tagId !== id && !subcategoriesIds.includes(tagId)));
        }
    };

    const onChangeSubcategoryHandle = ([parentId, id], isAdd) => {
        const tagsIds = Array.isArray(transaction.tags_ids) ? transaction.tags_ids : [];
        if (isAdd) {
            const idsToAdd = tagsIds.some((tagId) => tagId === parentId) ? [id] : [parentId, id];
            updateTransactionsTags([...tagsIds, ...idsToAdd]);
            onTagsChange?.({ tagId: id });
        } else {
            updateTransactionsTags(tagsIds.filter((tagId) => tagId !== id));
        }
    };

    const updateTransactionsTags = (tagsSelectedIds) => {
        updateTransaction({ taxonomies_tags_ids: tagsSelectedIds });
    };

    useEffect(() => {
        if (!isTaxonomiesLoading) {
            setCurrentTaxonomy(taxonomies[0]?.id ?? '');
        }
    }, [isTaxonomiesLoading]);

    const handleAddTag = () => {
        const randomColor = Math.floor(Math.random() * CATEGORY_COLORS.length);
        const color = CATEGORY_COLORS[randomColor];

        open(DRAWER_STACK.TAG_EDITOR, {
            title: t('create-tag'),
            tag: {
                name: '',
                color,
                icon: '💰',
            },
            onSubmit: async (data) => {
                const tag = await createCategory(data);
                await queryClient.invalidateQueries({ queryKey: ['categories'] });

                const taxonomies_tags_ids = [...(transaction.tags_ids || []), tag.id];
                updateTransaction({ taxonomies_tags_ids });
                setCurrentTaxonomy(tag.taxonomy_id);
                onTagsChange?.({ tagId: tag.id });
            },
        });
    };

    const handleAddSubTag = (tag) => {
        open(DRAWER_STACK.SUBTAG_EDITOR, {
            title: t('create-subTag'),
            onSubmit: async (data) => {
                const subtag = await createCategory({
                    icon: tag.icon,
                    color: tag.color,
                    parent_tag_id: tag.id,
                    taxonomy_id: tag.taxonomy_id,
                    ...data,
                });
                await queryClient.invalidateQueries({ queryKey: ['categories'] });

                onChangeSubcategoryHandle([tag.id, subtag.id], true);
                setCurrentTaxonomy(subtag.taxonomy_id);
            },
            subTag: {},
        });
    };
    const isLoading = isTransactionLoading || isTaxonomiesLoading;
    const isExpense = amount * (transaction.reverse_sign ? -1 : 1) < 0;

    const handleChangeTaxonomy = (id) => {
        setFilterText('');
        setCurrentTaxonomy(id);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-6">
                <Loading />
            </div>
        );
    }
    return (
        <>
            <DrawerStackSavingIndicator saving={isUpdating} />

            <div className="pb-6">
                <div className="flex items-center gap-1.5 mb-1">
                    <img src={provider_connection_product?.provider_logo_url} width={20} height={20} alt="" />
                    <div>
                        <div className="font-semibold text-sm">{provider_connection_product?.name}</div>
                    </div>
                </div>

                <div className="flex justify-between items-start gap-3 text-lg font-semibold text-dark-blue">
                    <div>{description}</div>
                    <div
                        className={cn(
                            'flex justify-end gap-0.5 items-center font-mono font-medium',
                            !isExpense && 'text-income',
                        )}
                    >
                        {transaction.reverse_sign && (
                            <Tooltip label={t('modified-sign')}>
                                <span className="flex">
                                    <Icon name="reverse" size={16} color="currentColor" className="mr-1" />
                                </span>
                            </Tooltip>
                        )}
                        {formatCurrency(amount, {
                            currency: transaction.currency,
                            locale,
                        })}
                    </div>
                </div>
            </div>

            {taxonomies.length > 1 && (
                <Tabs value={currentTaxonomy} onChange={handleChangeTaxonomy}>
                    {taxonomies.map((taxonomy) => (
                        <Tab key={taxonomy.id} value={taxonomy.id}>
                            {taxonomy.name}
                        </Tab>
                    ))}
                </Tabs>
            )}

            <div className="py-3 flex items-center gap-2">
                <Search
                    placeholder={t('search-placeholder')}
                    className="grow"
                    value={filterText}
                    onChange={setFilterText}
                    data-autofocus="true"
                />

                <IconButton tooltip={t('add-category')} icon={<OutlineAdd />} onClick={handleAddTag} />
            </div>

            <div>
                {categoriesFiltered.map((category) => (
                    <Fragment key={category.id}>
                        <div className="group flex items-center py-1.5">
                            <label className="flex grow gap-4 items-center">
                                <Checkbox
                                    checked={transaction.tags_ids?.includes(category.id)}
                                    label={category.name}
                                    onChange={(e) => onChangeHandle(category.id, e.target.checked)}
                                />
                                <div
                                    className="rounded-lg aspect-square grid place-content-center text-xl w-[42px]"
                                    style={{ backgroundColor: category.bgColor }}
                                >
                                    {category.icon}
                                </div>
                                <div className="font-bold" style={{ color: category.color }}>
                                    {category.name}
                                </div>
                            </label>

                            <Tooltip label={t('add-subcategory')}>
                                <button
                                    style={{ backgroundColor: category.bgColor }}
                                    className={cn(
                                        'inline-flex size-6 items-center justify-center rounded-full',
                                        '[&_svg]:pointer-events-none [&_svg]:shrink-0',
                                        'md:opacity-0 group-hover:opacity-100 transition-opacity',
                                    )}
                                    onClick={() => handleAddSubTag(category)}
                                >
                                    <OutlineAdd size={18} />
                                </button>
                            </Tooltip>
                        </div>
                        {category.subcategories.length > 0 && (
                            <div className="flex flex-wrap gap-x-1.5 gap-y-1 mb-5 pl-10">
                                {category.subcategories.map((subcategory) => (
                                    <div key={subcategory.id}>
                                        <button
                                            style={{ '--color': subcategory.color }}
                                            type="button"
                                            onClick={() =>
                                                onChangeSubcategoryHandle(
                                                    [category.id, subcategory.id],
                                                    !transaction.tags_ids?.includes(subcategory.id),
                                                )
                                            }
                                            className={cn(
                                                'inline-flex text-xs px-3 py-1 rounded-full text-[var(--color)] inset-ring-1 inset-ring-current/50',
                                                transaction.tags_ids?.includes(subcategory.id) &&
                                                    'bg-[var(--color)] text-white inset-ring-0',
                                            )}
                                        >
                                            {subcategory.name}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Fragment>
                ))}
            </div>
        </>
    );
}

TransactionTagsEditorDrawer.propTypes = {
    transactionId: PropTypes.string.isRequired,
    onTagsChange: PropTypes.func,
};
